import { useParams } from "react-router-dom";
import { apiUrls } from "../../actprep-ui/utils/urls";
import { dataGridNestedColumnGetter } from "../../actprep-ui/utils/tables";
import PaginatedTable from "../../actprep-ui/components/PaginatedTable";
import { sexyBooleanColumn } from "../../actprep-ui/constants/dataGrid";
import { useCallback } from "react";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { fullName } from "../../actprep-ui/utils/formatting";
import { showSuccessSnackbar } from "../../actprep-ui/utils/feedback";

const goodNewsTextIntro = (item, i) => {
  if (item.student.school) {
    return `${i + 1}. ${fullName(item.student)} (${
      item.student.school?.name || "No School"
    })${item.current_team ? `(${item.current_team})` : ""}`;
  }
  return `${i + 1}. ${fullName(item.student)} ${
    item.current_team ? `(${item.current_team})` : ""
  }`;
};

const isReviewWeek = (weekString) => {
  const [subject] = weekString.split("_");
  return (
    ["english", "math", "review"].includes(subject) && weekString.includes("ft")
  );
};

const sortDesc = (data, metric) => {
  return [...data.sort((a, b) => b[metric] - a[metric])];
};

export default function LTScoreGoodNews({ filters }) {
  const { goodNewsType } = useParams();
  const tableApiRef = useGridApiRef();

  const copyGoodNews = useCallback(async () => {
    const data = tableApiRef.current.getSortedRows();
    const reviewWeek = data.length ? isReviewWeek(data[0].class_week) : false;
    const scoreData = sortDesc(sortDesc(data, "total_increase"), "best_score")
      .map((item, i) => {
        const startScore = item.student[`start_${item.class_subject}`];
        return `${goodNewsTextIntro(item, i)}: ${startScore}-${
          item.best_score
        } (${item.total_increase} point increase)`;
      })
      .join("\n");
    const increaseData = sortDesc(
      sortDesc(data, "best_score"),
      "total_increase"
    )
      .map((item, i) => {
        const startScore = item.student[`start_${item.class_subject}`];
        return (
          `${goodNewsTextIntro(item, i)}: ${
            item.total_increase
          } point increase` +
          (reviewWeek ? "" : ` - ${startScore}-${item.best_score}`) +
          ` - ${item.increase} this week`
        );
      })
      .join("\n");
    const hoursData = sortDesc(sortDesc(data, "total_increase"), "actual_hours")
      .map((item, i) => {
        return `${goodNewsTextIntro(item, i)}: ${parseFloat(
          item.actual_hours
        ).toFixed(2)}`;
      })
      .join("\n");

    const fullText =
      "Leadership Team Good News:\n\n" +
      (reviewWeek ? "" : `Score:\n${scoreData}\n\n`) +
      `Increase:\n${increaseData}\n\nHours:\n${hoursData}`;
    await navigator.clipboard.writeText(fullText);
    showSuccessSnackbar("Copied!");
  }, [tableApiRef]);

  const columns = [
    {
      field: "student.first_name",
      headerName: "First name",
      valueGetter: dataGridNestedColumnGetter,
    },
    {
      field: "student.last_name",
      headerName: "Last name",
      valueGetter: dataGridNestedColumnGetter,
    },
    {
      field: "total_increase",
      headerName: "Total Increase",
    },
    {
      field: "increase",
      headerName: "This Week Increase",
    },
    {
      field: "start_score",
      headerName: "Start Score",
    },
    {
      field: "best_score",
      headerName: "Best Score",
    },
    {
      field: "quiz_average",
      headerName: "Quiz Average",
      valueFormatter: (val) => Math.round(val),
    },
    {
      field: "student.school.name",
      headerName: "School",
      valueGetter: dataGridNestedColumnGetter,
    },
    {
      field: "current_team",
      headerName: "Team",
      valueGetter: dataGridNestedColumnGetter,
    },
    {
      field: "in_the_game",
      headerName: "In The Game",
      renderCell: sexyBooleanColumn,
    },
  ];
  return (
    <PaginatedTable
      apiRef={tableApiRef}
      pagination={false}
      tableName={"Score Good News"}
      apiUrl={apiUrls.admin.goodNews(goodNewsType)}
      urlParams={{
        search_params: filters,
      }}
      columns={columns}
      alwaysShowActions
      actions={[
        {
          label: "Copy Text Message",
          onClick: copyGoodNews,
        },
      ]}
    />
  );
}
